import { CameraData, ModelFace } from "@centricsoftware/model-viewer";
import { ControllerInterface } from "csi/action/Controller";
import comm from "csi/comm";
import JsonResponse from "csi/comm/JsonResponse";
import { finalizeReactNewRequest } from "csi/middleware/common";
import { submitWriter } from "csi/service/writerService";
import { uploadCameraPositionPreview } from "csi/state/3D/snapshotsService";
import { NewInterface } from "csi/table/NewInterface";
import { cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { numberRound } from "csi/utility/numberUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { action, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";

export interface ExisitingCameraPositionData {
	active: boolean;
	cameraData: CameraData;
	description: string;
	name: string;
	url: cnl;
}
type ModelViewerGetCanvasBlob = () => Promise<Blob>;
type ModelViewerSetCameraData = (data: CameraData) => void;

interface FaceData {
	color: string;
	res: string;
	cameraData: CameraData;
}

// The position and rotation data for each face of the cube mirrors the data from the system type camera positions
export const CUBE_FACE_DATA: Record<Exclude<ModelFace, ModelFace.Isometric>, FaceData> = {
	[ModelFace.Front]: {
		color: "#8F79E8",
		res: "3DFront",
		cameraData: {
			position: [0, 0, 2.000],
			rotation: [0, 0, 0],
		},
	},
	[ModelFace.Back]: {
		color: "#52C47A",
		res: "3DBack",
		cameraData: {
			position: [0, 0, -2.000],
			rotation: [3.1415, 0, 3.1415],
		},
	},
	[ModelFace.Left]: {
		color: "#FCCD30",
		res: "3DLeft",
		cameraData: {
			position: [-2.000, 0, 0],
			rotation: [0, -1.5707, 0],
		},
	},
	[ModelFace.Right]: {
		color: "#61A7F2",
		res: "3DRight",
		cameraData: {
			position: [2.000, 0, 0],
			rotation: [0, 1.5707, 0],
		},
	},
	[ModelFace.Top]: {
		color: "#FAA243",
		res: "3DTop",
		cameraData: {
			position: [0, 2.000, 0],
			rotation: [-1.5707, 0, 0],
		},
	},
	[ModelFace.Bottom]: {
		color: "#EB5E96",
		res: "3DBottom",
		cameraData: {
			position: [0, -2.000, 0],
			rotation: [1.5707, 0, 0],
		},
	},
};

export default class CameraPositionDialogStore extends ReactionStore {
	/** Camera position name */
	@observable cameraPositionName: string = "";

	/** Camera position description */
	@observable cameraPositionDescription: string = "";

	/** Camera position & rotation data from the model-viewer orbit controls */
	@observable modelViewerCameraData: CameraData;

	/** Flag to determine if image is saving */
	@observable isSaving: boolean = false;

	/** Callback for modelViewer gives a blob of the canvas back on save */
	@observable.ref modelViewerGetCanvasBlob: ModelViewerGetCanvasBlob = null;

	/** Method from ModelViewer to set the camera data in the viewer */
	@observable.ref modelViewerSetCameraData: ModelViewerSetCameraData = null;

	/** Save error */
	@observable saveErrorMessage: string | null = "";

	/** handles saving data on the form */
	private readonly onSave: (jsonResponse: JsonResponse) => void;

	/** handles closing the form */
	readonly onCancel: VoidFunction;

	/** writer object from the controller */
	readonly writer: Writer;

	/** action controller object to get nodes from */
	readonly actionController: ControllerInterface;

	/** Inital Camera Position Data if user is editing an exisiting camera position */
	readonly initialCameraData: CameraData;

	readonly cameraActiveFlag: boolean;

	/** Exisitng Camera Position Url if the user is editing an exisiting camera position */
	readonly exisitngCameraPositionUrl: cnl = null;

	/** Determine if the camera position can be saved */
	get userCanSaveNewCameraPosition(): boolean {
		return (!this.cameraPositionName || this.cameraPositionName.trim() === "") || this.isSaving;
	};

	/** set the saving flag */
	@action setIsSaving = (isSaving: boolean): void => {
		this.isSaving = isSaving;
	};

	/** Set camera position name */
	@action setCameraPositionName = (e): void => {
		const name = e.target.value || "";
		this.cameraPositionName = name;
	};

	/** Set camera position description */
	@action setCameraPositionDescription = (e): void => {
		const description = e.target.value || "";
		this.cameraPositionDescription = description;
	};

	/** Set CameraData from modelviewer */
	@action setCameraData = (data: CameraData): void => {
		const roundedData = {
			position: [
				numberRound(data.position[0], 3),
				numberRound(data.position[1], 3),
				numberRound(data.position[2], 3),
			],
			rotation: [
				numberRound(data.rotation[0], 3),
				numberRound(data.rotation[1], 3),
				numberRound(data.rotation[2], 3),
			],
		} as CameraData;
		this.modelViewerCameraData = roundedData;
	};

	/** set save error */
	@action setSaveErrorMessage = (error: string): void => {
		this.saveErrorMessage = error;
	};

	/** Save Camera position to new or exisiting node */
	@action saveCameraData = async (): Promise<void> => {
		const { exisitngCameraPositionUrl } = this;
		const camPosUrl = exisitngCameraPositionUrl || comm.createUrl();

		this.saveCameraPosition(camPosUrl);
	};

	/** Save a Camera Position */
	@action saveCameraPosition = async (camPosUrl: string): Promise<boolean> => {
		const { cameraPositionName, cameraPositionDescription, modelViewerCameraData } = this;
		try {
			const writer: Writer = this.writer;
			writer.createNode(camPosUrl, "CameraPosition", null, null, true)
				.changeAttribute("Node Name", xml.ATTR_STRING, cameraPositionName)
				.changeAttribute("Description", xml.ATTR_STRING, cameraPositionDescription)
				.changeAttribute("Location", xml.ATTR_DOUBLEVECTOR, modelViewerCameraData.position)
				.changeAttribute("Rotation", xml.ATTR_DOUBLEVECTOR, modelViewerCameraData.rotation)
				.changeAttribute("Active", xml.ATTR_BOOLEAN, this.cameraActiveFlag);

			writer.validateNode(camPosUrl, true);
			await submitWriter(writer);

			const canvasBlob = await this.modelViewerGetCanvasBlob();
			const jsonResponse = await uploadCameraPositionPreview(canvasBlob, cameraPositionName, camPosUrl);

			if (!jsonResponse) {
				return false;
			}

			this.onSave(jsonResponse);
			finalizeReactNewRequest(this.actionController as NewInterface, camPosUrl);
			this.setIsSaving(false);
			return true;
		} catch (error) {
			this.setSaveErrorMessage(error.message);
			this.setIsSaving(false);
			return false;
		}
	};

	constructor(onSave, onCancel: VoidFunction, writer: Writer, actionController: ControllerInterface, exisitngCameraPositionData?: ExisitingCameraPositionData) {
		super();
		makeObservable(this);
		this.onSave = onSave;
		this.onCancel = onCancel;
		this.writer = writer;
		this.actionController = actionController;
		if (exisitngCameraPositionData) {
			const { cameraData, url, name, description, active } = exisitngCameraPositionData;
			this.initialCameraData = cameraData;
			this.cameraActiveFlag = active;
			this.exisitngCameraPositionUrl = url;
			this.cameraPositionName = name;
			this.cameraPositionDescription = description;
		}
	}
}

export const CameraPositionDialogStoreContext = createContext<CameraPositionDialogStore>(undefined);
export const useCameraPositionDialogStore = (): CameraPositionDialogStore => useContext(CameraPositionDialogStoreContext);